import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useSystemStore } from '@/stores/system'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/documents/:type(terms|privacy).html',
    name: 'AgreementHtml',
    component: () => import('@/views/LegalDocument.vue'),
    meta: { title: '协议正文', requiresAuth: false, standalone: true }
  },
  {
    path: '/legal/:type(terms|privacy)',
    name: 'LegalDocument',
    component: () => import('@/views/LegalDocument.vue'),
    meta: { title: '协议阅读', requiresAuth: false }
  },
  {
    path: '/legal/preview',
    name: 'LegalPdfPreview',
    component: () => import('@/views/LegalPdfPreview.vue'),
    meta: { title: '协议预览', requiresAuth: false }
  },
  { path: '/privacy', redirect: '/legal/privacy' },
  { path: '/terms', redirect: '/legal/terms' },
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomePage.vue'),
    meta: {
      title: '梦之窗窗帘定制',
      requiresAuth: false
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      title: '管理后台',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: {
      title: '用户登录',
      requiresAuth: false,
      hideForAuth: true
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue'),
    meta: {
      title: '用户注册',
      requiresAuth: false,
      hideForAuth: true
    }
  },
  {
    path: '/designer2d',
    name: 'Designer2D',
    component: () => import('@/views/Designer2D.vue'),
    meta: {
      title: '2D窗帘设计器',
      requiresAuth: true
    }
  },
  {
    path: '/designer',
    name: 'Designer',
    component: () => import('@/views/Designer.vue'),
    meta: {
      title: '3D窗帘设计器',
      requiresAuth: true,
      feature: '3d_design'
    }
  },
  {
    path: '/designer-test',
    name: 'DesignerTest',
    component: () => import('@/views/Designer-test.vue'),
    meta: {
      title: '3D引擎测试',
      requiresAuth: false,
      feature: '3d_design'
    }
  },
  {
    path: '/products',
    name: 'Products',
    component: () => import('@/views/Products.vue'),
    meta: {
      title: '产品中心',
      requiresAuth: false
    }
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('@/views/Orders.vue'),
    meta: {
      title: '我的订单',
      requiresAuth: true
    }
  },
  {
    path: '/order/checkout',
    name: 'OrderCheckout',
    component: () => import('@/views/OrderCheckout.vue'),
    meta: {
      title: '确认订单',
      requiresAuth: true
    }
  },
  {
    path: '/order/:id',
    name: 'OrderDetail',
    component: () => import('@/views/OrderDetail.vue'),
    meta: {
      title: '订单详情',
      requiresAuth: true
    }
  },
  {
    path: '/order/success/:id',
    name: 'OrderSuccess',
    component: () => import('@/views/OrderSuccess.vue'),
    meta: {
      title: '下单成功',
      requiresAuth: true
    }
  },
  {
    path: '/order/failure',
    name: 'OrderFailure',
    component: () => import('@/views/OrderFailure.vue'),
    meta: {
      title: '下单失败',
      requiresAuth: true
    }
  },
  {
    path: '/users',
    name: 'UserManagement',
    component: () => import('@/views/UserManagement.vue'),
    meta: {
      title: '用户管理',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/settings',
    name: 'SiteSettings',
    component: () => import('@/views/SiteSettings.vue'),
    meta: {
      title: '网站设置',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/admin/products',
    name: 'ProductManagement',
    component: () => import('@/views/ProductManagement.vue'),
    meta: {
      title: '产品管理',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/admin/orders',
    name: 'AdminOrders',
    component: () => import('@/components/admin/AdminOrders.vue'),
    meta: {
      title: '订单管理（后台）',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/admin/fabrics',
    name: 'FabricManagement',
    component: () => import('@/views/FabricManagement.vue'),
    meta: {
      title: '面料库管理',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/admin/design-management',
    name: 'DesignManagement',
    component: () => import('@/views/DesignManagement.vue'),
    meta: {
      title: '窗帘设计管理',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/AboutUs.vue'),
    meta: {
      title: '关于我们',
      requiresAuth: false
    }
  },
  {
    path: '/cases',
    name: 'Cases',
    component: () => import('@/views/Cases.vue'),
    meta: {
      title: '案例展示',
      requiresAuth: false
    }
  },
  {
    path: '/cases/:id',
    name: 'CaseDetail',
    component: () => import('@/views/CaseDetail.vue'),
    meta: {
      title: '案例详情',
      requiresAuth: false
    }
  },
  {
    path: '/product/:id',
    name: 'ProductDetail',
    component: () => import('@/views/ProductDetail.vue'),
    meta: {
      title: '产品详情',
      requiresAuth: false
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      title: '个人资料',
      requiresAuth: true
    }
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: () => import('@/views/Favorites.vue'),
    meta: {
      title: '我的收藏',
      requiresAuth: true
    }
  },
  {
    path: '/designs2d',
    name: 'Designs2D',
    component: () => import('@/views/Designs2D.vue'),
    meta: {
      title: '我的2D设计',
      requiresAuth: true
    }
  },
  {
    path: '/designs',
    name: 'Designs',
    component: () => import('@/views/Designs.vue'),
    meta: {
      title: '我的3D设计',
      requiresAuth: true,
      feature: '3d_design'
    }
  },
  // 推广功能路由
  {
    path: '/admin/promotion',
    name: 'PromotionManagement',
    component: () => import('@/components/admin/PromotionManagement.vue'),
    meta: {
      title: '推广统计管理',
      requiresAuth: true,
      adminOnly: true
    }
  },
  {
    path: '/admin/reviews',
    name: 'AdminReviews',
    component: () => import('@/components/admin/AdminReviews.vue'),
    meta: {
      title: '评价管理',
      requiresAuth: true,
      adminOnly: true
    }
  },
  // 404页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/HomePage.vue'),
    meta: {
      title: '页面未找到'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return { el: to.hash, top: 24 }
    } else {
      return { top: 0 }
    }
  }
})

// 全局前置守卫
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const systemStore = useSystemStore()

  // 设置页面标题
  if (to.meta?.title) {
    document.title = `${to.meta.title} - 梦之窗窗帘定制系统`
  }

  // 检查功能是否启用
  if (to.meta?.feature === '3d_design') {
    if (!systemStore.featureConfig.enable3DDesign) {
      // 如果3D设计被禁用，重定向到2D设计
      const redirectMap: Record<string, string> = {
        'Designer': 'Designer2D',
        'Designs': 'Designs2D'
      }
      const redirectName = redirectMap[to.name as string]
      if (redirectName) {
        next({ name: redirectName })
        return
      }
    }
  }

  // 检查是否需要认证
  if (to.meta?.requiresAuth) {
    if (!userStore.isLoggedIn) {
      next({
        name: 'Login',
        query: { redirect: to.fullPath }
      })
      return
    }
  }

  // 检查是否需要管理员权限
  if (to.meta?.adminOnly) {
    console.log('🔒 检查管理员权限:', {
      path: to.path,
      isAdmin: userStore.isAdmin,
      userType: userStore.userInfo?.userType,
      roles: userStore.roles
    })

    if (!userStore.isAdmin) {
      console.warn('❌ 无管理员权限，重定向到首页')
      next({ name: 'Home' })
      return
    }
  }

  // 已登录用户访问登录/注册页面时重定向
  if (to.meta?.hideForAuth && userStore.isLoggedIn) {
    next({ name: 'Home' })
    return
  }

  next()
})

export default router
